// VideoCard.jsx

import React, { useEffect, useRef, useState } from 'react';

// Utilities
import { getNodeSubtitle } from 'utilities/NodeSubtitle';

// Services
import { resolvePlayableVideoUrl } from 'services/storage/StorageAccessService';

// CSS
import './VideoCard.css';

/**
 * VideoCard
 * 
 * Displays a video card for a given video node.
 *
 * @param {object} node - The photo node to display.
 *
 * @returns {ReactNode} VideoCard.
 */
const VideoCard = ({ node, onClick }) => {

    // ----- REFS ----- //

    // Video Element Ref

    const videoRef = useRef(null);

    // Observer Ref
    const observerRef = useRef(null);

    // ----- STATE ----- //

    // Visibility State

    const [isVisible, setIsVisible] = useState(false);

    // Hover State
    const [isHovered, setIsHovered] = useState(false);

    // Resolved media src
    const [videoSrc, setVideoSrc] = useState(null);

    // ----- EFFECTS ----- //

    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const shouldPlay = entry.isIntersecting && entry.intersectionRatio > 0.25;
                    setIsVisible(shouldPlay);
                });
            },
            {
                root: null,
                rootMargin: '0px 0px 200px 0px',
                threshold: [0, 0.25, 0.5, 0.75, 1],
            }
        );

        observer.observe(videoEl);
        observerRef.current = observer;

        return () => {
            observer.disconnect();
            observerRef.current = null;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const url = await resolvePlayableVideoUrl(node);
            if (cancelled) return;
            setVideoSrc(url || null);
        })();

        return () => {
            cancelled = true;
        };
    }, [node]);

    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl) return;

        if (isVisible) {
            const playPromise = videoEl.play();
            if (playPromise?.catch) {
                playPromise.catch(() => {
                    /* noop: autoplay may be prevented; user interaction will trigger */
                });
            }
        } else {
            videoEl.pause();
        }
    }, [isVisible]);

    if (!node) return null;

    // ----- RENDER ----- //

    return (

        <div
          className="video-card-container"
          role={typeof onClick === 'function' ? 'button' : undefined}
          tabIndex={typeof onClick === 'function' ? 0 : undefined}
          onClick={onClick}
          onKeyDown={(event) => {
                if (!onClick) return;
                if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                    event.preventDefault();
                    onClick();
                }
            }}
        >

            {/* VIDEO */}
            <video
              className="video-card-video"
              src={videoSrc || undefined}
              ref={videoRef}
              loop
              muted
              playsInline
              controls={isVisible && isHovered}
              preload="metadata"
              onBlur={() => setIsHovered(false)}
              onFocus={() => setIsHovered(true)}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />

            {/* TITLE */}
            <div
              className="video-card-title"
            >
                {node?.title}
            </div>

            {/* DESCRIPTION */}
            {getNodeSubtitle(node) &&

                <div
                  className="video-card-description"
                >
                    {getNodeSubtitle(node)}
                </div>
            }

        </div>

    );
};

// Dev Tools
VideoCard.displayName = 'VideoCard';

export default VideoCard;
